/**
 * Game Over Screen Component
 * Displays final score and restart button
 */

import { GameMode } from '../core/GameEngine';

interface GameOverScreenProps {
  score: number;
  highScore: number;
  level: number;
  mode: GameMode;
  onRestart: () => void;
  onBackToModeSelect?: () => void;
}

export function GameOverScreen({ score, highScore, level, mode, onRestart, onBackToModeSelect }: GameOverScreenProps) {
  const isNewHighScore = score === highScore && score > 0;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(0, 0, 0, 0.9)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '24px',
      zIndex: 1000,
      animation: 'fadeIn 300ms ease-in-out'
    }}>
      <h1 style={{
        fontFamily: 'Syne, sans-serif',
        fontSize: '48px',
        fontWeight: 800,
        letterSpacing: '-2px',
        color: 'var(--accent-r)',
        margin: 0,
        textAlign: 'center'
      }}>
        GAME OVER
      </h1>

      {isNewHighScore && (
        <p style={{
          color: 'var(--accent-y)',
          fontSize: '18px',
          fontWeight: 600,
          margin: 0,
          animation: 'pulse 1s ease-in-out infinite'
        }}>
          🎉 NEW HIGH SCORE! 🎉
        </p>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(90px, 1fr))',
        gap: '12px',
        width: 'min(100%, 360px)',
        textAlign: 'center'
      }}>
        <div style={{
          padding: '14px 12px',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px' }}>Score</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text)', fontFamily: 'Syne, sans-serif' }}>{score}</div>
        </div>
        <div style={{
          padding: '14px 12px',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px' }}>Best</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--accent-b)', fontFamily: 'Syne, sans-serif' }}>{highScore}</div>
        </div>
        <div style={{
          padding: '14px 12px',
          borderRadius: '12px',
          background: 'rgba(255,255,255,0.04)',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <div style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px' }}>Level</div>
          <div style={{ fontSize: '28px', fontWeight: 800, color: 'var(--accent-g)', fontFamily: 'Syne, sans-serif' }}>{level}</div>
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        marginTop: '20px'
      }}>
        <button
          onPointerDown={onRestart}
          style={{
            padding: '16px 40px',
            fontSize: '18px',
            fontWeight: 700,
            fontFamily: 'Syne, sans-serif',
            color: 'var(--bg)',
            background: 'var(--accent-g)',
            border: 'none',
            borderRadius: '12px',
            cursor: 'pointer',
            boxShadow: '0 0 30px rgba(0,229,155,0.5)',
            transition: 'transform 150ms ease, box-shadow 150ms ease',
            touchAction: 'none'
          }}
          onPointerEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.05)';
            e.currentTarget.style.boxShadow = '0 0 40px rgba(0,229,155,0.7)';
          }}
          onPointerLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 0 30px rgba(0,229,155,0.5)';
          }}
        >
          REPLAY {mode.toUpperCase()}
        </button>

        {onBackToModeSelect && (
          <button
            onPointerDown={onBackToModeSelect}
            style={{
              padding: '12px 32px',
              fontSize: '14px',
              fontWeight: 600,
              fontFamily: 'Syne, sans-serif',
              color: 'var(--text)',
              background: 'transparent',
              border: '1px solid var(--border)',
              borderRadius: '12px',
              cursor: 'pointer',
              transition: 'all 150ms ease',
              touchAction: 'none'
            }}
            onPointerEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
              e.currentTarget.style.borderColor = 'var(--text)';
            }}
            onPointerLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.borderColor = 'var(--border)';
            }}
          >
            CHANGE MODE
          </button>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.8;
            transform: scale(1.05);
          }
        }
      `}</style>
    </div>
  );
}
